import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Card, CardContent, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Checkbox, Divider, Alert, Tooltip, Stack, IconButton, DialogContentText,
  List, ListItem, ListItemText, InputAdornment
} from '@mui/material';
import {
  ViewQuilt as KanbanIcon, PersonAdd as PersonAddIcon,
  Edit as EditIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon,
  Search as SearchIcon, Close as CloseIcon, People as PeopleIcon
} from '@mui/icons-material';
import { academyStore } from '../services/academyStore';
import { Group, GroupStatus } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';

const ENTITY_KEY = 'Blobtic_Academy,academy_group';
const ACTION_SUFFIX = '';
const PRIMARY_KEY = 'GRP_IDF';
const KANBAN_COLUMNS: GroupStatus[] = ['Upcoming', 'Ongoing', 'Completed'];

const fsx = {
  '& .MuiInputBase-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
};

const dtoToGroup = (dto: any): Group => ({
  id: dto.GRP_IDF,
  name: dto.GRP_NAME || '',
  course_id: dto.COURSE_IDF || 1,
  teacher_id: dto.TCH_IDF || 1,
  max_capacity: Number(dto.GRP_MAX_CAPACITY) || 20,
  start_date: dto.GRP_START_DATE ? new Date(dto.GRP_START_DATE).toISOString().split('T')[0] : '',
  expected_end_date: dto.GRP_EXPECTED_END_DATE ? new Date(dto.GRP_EXPECTED_END_DATE).toISOString().split('T')[0] : '',
  status: dto.GRP_STATUS || 'Upcoming',
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  dos_code: dto.DOS_CODE || undefined,
  dos_lib: dto.DOS_LIB || undefined,
  user_lib: dto.USER_LIB || undefined,
  last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

const groupToDto = (g: Partial<Group>): Record<string, any> => ({
  GRP_IDF: (g as any).GRP_IDF ?? g.id,
  GRP_NAME: g.name || '',
  COURSE_IDF: g.course_id || 1,
  TCH_IDF: g.teacher_id || 1,
  GRP_MAX_CAPACITY: g.max_capacity ?? 20,
  GRP_START_DATE: g.start_date || '',
  GRP_EXPECTED_END_DATE: g.expected_end_date || '',
  GRP_STATUS: g.status || 'Upcoming',
  ...(g.dos_idf ? { DOS_IDF: g.dos_idf } : {}),
});

const emptyGroup = (): Partial<Group> => ({
  name: '',
  course_id: 1,
  teacher_id: 1,
  max_capacity: 20,
  start_date: new Date().toISOString().split('T')[0],
  expected_end_date: new Date(Date.now() + 90 * 86400000).toISOString().split('T')[0],
  status: 'Upcoming',
});

export const GroupsPage: React.FC = () => {
  const { t } = useLanguage();
  const { getParam } = useSystemParameters();
  
  const currencySymbol = getParam('Currency_Symbol', 'DZD');

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [, setTick] = useState(0);

  const [formData, setFormData] = useState<Partial<Group>>(emptyGroup());
  const [openEnrollModal, setOpenEnrollModal] = useState<Group | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [enrollingStudentIds, setEnrollingStudentIds] = useState<number[]>([]);
  const [enrollSearchText, setEnrollSearchText] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    content: string;
    color?: 'primary' | 'error';
    onConfirm: () => void;
  }>({ open: false, title: '', content: '', onConfirm: () => { } });

  useEffect(() => {
    academyStore.ensureGroupsLoaded();
    academyStore.ensureCoursesLoaded();
    academyStore.ensureTeachersLoaded();
    academyStore.ensureStudentsLoaded();
    academyStore.ensureGroupStudentsLoaded();
    return academyStore.subscribe(() => setTick(x => x + 1));
  }, []);

  const groups = academyStore.getGroups();
  const courses = academyStore.getCourses();
  const teachers = academyStore.getTeachers();
  const students = academyStore.getStudents();
  const groupStudents = academyStore.getGroupStudents();

  const getCourseTitle = (courseId: number) => courses.find(c => c.id === courseId)?.title || '-';
  const getTeacherName = (teacherId: number) => teachers.find(t => t.id === teacherId)?.full_name || t('academy.groups.unassigned');
  const getEnrolledCount = (groupId: number) =>
    groupStudents.filter(gs => gs.group_id === groupId && gs.status === 'Enrolled').length;

  const filteredEnrollStudents = useMemo(() => {
    if (!enrollSearchText.trim()) return students;
    const q = enrollSearchText.toLowerCase().trim();
    return students.filter(s =>
      (s.first_name || '').toLowerCase().includes(q)
      || (s.last_name || '').toLowerCase().includes(q)
      || (s.email || '').toLowerCase().includes(q)
      || (s.phone || '').includes(q)
      || (`${s.first_name} ${s.last_name}`).toLowerCase().includes(q)
    );
  }, [students, enrollSearchText]);

  const openNew = () => {
    const initial = emptyGroup();
    if (courses[0]) initial.course_id = courses[0].id;
    if (teachers[0]) initial.teacher_id = teachers[0].id;
    setFormData(initial);
    setView('form');
  };

  const openEdit = (group: Group) => {
    setFormData({ ...group });
    setView('form');
  };

  const backToList = () => {
    setView('list');
    setFormData(emptyGroup());
  };

  const setField = <K extends keyof Group>(key: K, value: Group[K]) =>
    setFormData(prev => ({ ...prev, [key]: value }));

  const handleValidate = (): string | null => {
    if (!formData.name?.trim()) return t('academy.groups.val_name');
    if (!formData.course_id) return t('academy.groups.val_course');
    if (!formData.teacher_id) return t('academy.groups.val_teacher');
    if (formData.max_capacity == null || formData.max_capacity <= 0) return t('academy.groups.val_capacity');
    return null;
  };

  // GenericPage auto-updates IndexedDB → academyStore refreshes via subscription.
  const handleDataChange = () => backToList();

  const handleClear = () => backToList();


  const handleDeleteGroup = (group: Group) => {
    const groupSessions = academyStore.getSessions().filter(s => s.group_id === group.id);
    const sessionCount = groupSessions.length;
    const isCascadeAllowed = academyStore.getDeleteGroupCascadeSessions(group.dos_idf);

    if (sessionCount > 0 && !isCascadeAllowed) {
      blobticMessage.error(
        t('common.confirm.title.delete') || 'Suppression bloquée',
        t('academy.groups.blocked_delete_has_sessions', group.name, sessionCount)
      );
      return;
    }

    const dialogContent = sessionCount > 0
      ? t('academy.groups.confirm_delete_with_sessions', group.name, sessionCount)
      : (t('academy.groups.confirm_delete', group.name) || `Voulez-vous supprimer le groupe « ${group.name} » ?`);

    setConfirmDialog({
      open: true,
      title: t('common.confirm.title.delete') || 'Confirmer la suppression',
      content: dialogContent,
      color: 'error',
      onConfirm: async () => {
        const res = await academyStore.deleteGroup(group.id, group.dos_idf);
        if (res.success) {
          blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
        } else {
          blobticMessage.error('Erreur suppression', res.message || 'Échec de la suppression.');
        }
      },
    });
  };

  const handleOpenEnroll = (grp: Group) => {
    setOpenEnrollModal(grp);
    setErrorMessage(null);
    setEnrollSearchText('');
    const existingEnrolledIds = groupStudents.filter(gs => gs.group_id === grp.id).map(gs => gs.student_id);
    setSelectedStudentIds(existingEnrolledIds);
  };

  const handleStudentEnrollToggle = async (studentId: number, checked: boolean) => {
    if (!openEnrollModal || enrollingStudentIds.includes(studentId)) return;

    const previousSelected = selectedStudentIds;
    setSelectedStudentIds(prev =>
      checked ? [...prev, studentId] : prev.filter(id => id !== studentId)
    );
    setEnrollingStudentIds(prev => [...prev, studentId]);

    try {
      const res = checked
        ? await academyStore.enrollStudentToGroup(openEnrollModal.id, studentId)
        : await academyStore.unenrollStudentFromGroup(openEnrollModal.id, studentId);
      if (!res.success) {
        setSelectedStudentIds(previousSelected);
        const fallback = t(checked ? 'academy.groups.error.enroll' : 'academy.groups.error.unenroll');
        const msg = res.message === 'UNENROLL_HAS_PAYMENTS'
          ? t('academy.groups.error.unenroll_has_payments')
          : (res.message || fallback);
        blobticMessage.error(t('common.error'), msg);
      }
    } catch {
      setSelectedStudentIds(previousSelected);
      blobticMessage.error(
        t('common.error'),
        t(checked ? 'academy.groups.error.enroll' : 'academy.groups.error.unenroll')
      );
    } finally {
      setEnrollingStudentIds(prev => prev.filter(id => id !== studentId));
    }
  };

  const handleStatusChange = async (group: Group, newStatus: GroupStatus) => {
    setErrorMessage(null);
    const res = await academyStore.updateGroupStatus(group.id, newStatus);
    if (!res.success) {
      setErrorMessage(res.message || t('academy.groups.error.status_change'));
    }
  };

  const renderKanbanCard = (group: Group, colStatus: GroupStatus) => {
    const course = courses.find(c => c.id === group.course_id);
    const teacher = teachers.find(te => te.id === group.teacher_id);
    const enrolledCount = getEnrolledCount(group.id);

    return (
      <Card key={group.id} variant="outlined" sx={{ mb: 2, borderRadius: 2.5, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1, pr: 1 }}>
              {group.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton size="small" color="primary" onClick={() => openEdit(group)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => handleDeleteGroup(group)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {t('academy.groups.course')} {course?.title} ({currencySymbol}{course?.price})
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {t('academy.groups.instructor')} {teacher?.full_name || t('academy.groups.unassigned')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {t('academy.groups.timeline')} {group.start_date} → {group.expected_end_date}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="caption" fontWeight="bold">
              {t('academy.groups.enrolled_count', enrolledCount, group.max_capacity)}
            </Typography>
            <Button size="small" variant="outlined" startIcon={<PersonAddIcon />} onClick={() => handleOpenEnroll(group)}>
              {t('academy.groups.bulk_enroll')}
            </Button>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">{t('academy.groups.move_stage')}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {colStatus !== 'Upcoming' && (
                <Button size="small" variant="text" onClick={() => handleStatusChange(group, 'Upcoming')}>
                  {t('academy.groups.status.upcoming')}
                </Button>
              )}
              {colStatus !== 'Ongoing' && (
                <Button size="small" variant="text" color="success" onClick={() => handleStatusChange(group, 'Ongoing')}>
                  {t('academy.groups.status.ongoing')}
                </Button>
              )}
              {colStatus !== 'Completed' && (
                <Button size="small" variant="contained" color="secondary" onClick={() => handleStatusChange(group, 'Completed')}>
                  {t('academy.groups.complete_group')}
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const tableData = groups.map(g => ({
    id: g.id,
    name: g.name,
    course: getCourseTitle(g.course_id),
    teacher: getTeacherName(g.teacher_id),
    status: g.status,
    start_date: g.start_date,
    expected_end_date: g.expected_end_date,
    enrolled: `${getEnrolledCount(g.id)} / ${g.max_capacity}`,
    _group: g,
  }));

  // ─── LIST VIEW ──────────────────────────────────────────────────────────────

  if (view === 'list') {
    return (
      <GenericPage
        title={t('academy.groups.title')}
        icon={<KanbanIcon />}
        footerEnabled={false}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onNew={openNew}
        newButtonLabel={t('academy.groups.add_new')}
      >

        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 3, borderRadius: 2 }}>
            {errorMessage}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 3, borderRadius: 2 }}>
            {successMessage}
          </Alert>
        )}

        {viewMode === 'grid' ? (
          <Grid container spacing={3}>
            {KANBAN_COLUMNS.map(colStatus => {
              const colGroups = groups.filter(g => g.status === colStatus);
              return (
                <Grid item xs={12} md={4} key={colStatus}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: 'action.hover',
                      minHeight: 500,
                      borderTop: `6px solid ${colStatus === 'Upcoming' ? '#3b82f6' : colStatus === 'Ongoing' ? '#10b981' : '#6b7280'}`
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {colStatus === 'Upcoming' ? t('academy.groups.status.upcoming') : colStatus === 'Ongoing' ? t('academy.groups.status.ongoing') : t('academy.groups.status.completed')} ({colGroups.length})
                      </Typography>
                      <Chip
                        label={colStatus === 'Upcoming' ? t('academy.groups.status.upcoming') : colStatus === 'Ongoing' ? t('academy.groups.status.ongoing') : t('academy.groups.status.completed')}
                        size="small"
                        color={colStatus === 'Upcoming' ? 'info' : colStatus === 'Ongoing' ? 'success' : 'default'}
                      />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {colGroups.map(group => renderKanbanCard(group, colStatus))}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <GenericTable
            columns={[
              { id: 'name', label: t('academy.groups.table.name'), minWidth: 180 },
              { id: 'course', label: t('academy.groups.table.course'), minWidth: 160 },
              { id: 'teacher', label: t('academy.groups.table.teacher'), minWidth: 140 },
              {
                id: 'status',
                label: t('academy.groups.table.status'),
                minWidth: 120,
                format: (val: any) => (
                  <Chip
                    label={val === 'Upcoming' ? t('academy.groups.status.upcoming') : val === 'Ongoing' ? t('academy.groups.status.ongoing') : t('academy.groups.status.completed')}
                    size="small"
                    color={val === 'Upcoming' ? 'info' : val === 'Ongoing' ? 'success' : 'default'}
                  />
                )
              },
              { id: 'start_date', label: t('academy.groups.start_date'), minWidth: 110 },
              { id: 'expected_end_date', label: t('academy.groups.expected_end_date'), minWidth: 110 },
              { id: 'enrolled', label: t('academy.groups.table.enrolled'), minWidth: 100 },
              {
                id: 'enroll_action',
                label: t('academy.groups.table.actions'),
                minWidth: 80,
                format: (_: any, row: any) => (
                  <Tooltip title={t('academy.groups.bulk_enroll')}>
                    <IconButton size="small" color="primary" onClick={() => handleOpenEnroll(row._group)}>
                      <PersonAddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              },
            ]}
            data={tableData}
            onEdit={(row) => openEdit((row as any)._group)}
            onDelete={(row) => handleDeleteGroup((row as any)._group)}
            filterColumns={['status']}
            searchPlaceholder={t('academy.groups.search_placeholder')}
            maxHeight="calc(100vh - 380px)"
          />
        )}

        <RootDialogPage
          open={!!openEnrollModal}
          onClose={() => { setOpenEnrollModal(null); setEnrollSearchText(''); }}
          title={t('academy.groups.enroll_title', openEnrollModal?.name ?? '')}
          icon={<PeopleIcon />}
          maxWidth="md"
          fullWidth
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 360 }}>
            <Typography variant="body2" color="text.secondary">
              {t('academy.groups.enroll_desc', `${currencySymbol}${courses.find(c => c.id === openEnrollModal?.course_id)?.price || 0}`)}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                placeholder={t('academy.groups.enroll_search_placeholder')}
                size="small"
                fullWidth
                value={enrollSearchText}
                onChange={(e) => setEnrollSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: enrollSearchText ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setEnrollSearchText('')}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                  sx: { borderRadius: 2 }
                }}
              />
              <Chip
                label={t('academy.groups.enroll_assigned_count', String(selectedStudentIds.length), String(students.length))}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 'bold', flexShrink: 0 }}
              />
            </Stack>

            {filteredEnrollStudents.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, opacity: 0.6 }}>
                <PeopleIcon sx={{ fontSize: 56, mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  {t('academy.groups.enroll_no_results')}
                </Typography>
              </Box>
            ) : (
              <List
                disablePadding
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'auto',
                  maxHeight: { xs: 'none', sm: 420 },
                  bgcolor: 'background.paper'
                }}
              >
                {filteredEnrollStudents.map((s) => {
                  const isChecked = selectedStudentIds.includes(s.id);
                  const isEnrolling = enrollingStudentIds.includes(s.id);
                  return (
                    <ListItem
                      key={s.id}
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: isChecked ? 'action.hover' : 'transparent',
                        '&:hover': { bgcolor: isChecked ? 'action.selected' : 'action.hover' }
                      }}
                      disabled={isEnrolling}
                      secondaryAction={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {isChecked && (
                            <Chip label={t('academy.students.status.enrolled')} size="small" color="primary" sx={{ height: 22, fontWeight: 'bold' }} />
                          )}
                          <Checkbox
                            edge="end"
                            checked={isChecked}
                            disabled={isEnrolling}
                            onClick={() => handleStudentEnrollToggle(s.id, !isChecked)}
                            disableRipple
                            color="primary"
                          />
                        </Stack>
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight={isChecked ? 600 : 400}>
                            {s.first_name} {s.last_name}
                          </Typography>
                        }
                        secondary={
                          <Stack direction="row" spacing={1} sx={{ mt: 0.3 }} flexWrap="wrap" alignItems="center">
                            {s.email && <Typography variant="caption" color="text.secondary">{s.email}</Typography>}
                            {s.phone && <Typography variant="caption" color="text.secondary">{s.phone}</Typography>}
                            {s.financial_hold && (
                              <Chip label={t('academy.students.hold_badge')} color="error" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                          </Stack>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </RootDialogPage>

        <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>
          <DialogTitle sx={{ fontWeight: 600 }}>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{confirmDialog.content}</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))} variant="outlined" color="inherit">
              {t('btn.cancel')}
            </Button>
            <Button
              onClick={() => {
                const action = confirmDialog.onConfirm;
                setConfirmDialog(prev => ({ ...prev, open: false }));
                action();
              }}
              autoFocus
              color={confirmDialog.color || 'primary'}
              variant="contained"
            >
              {t('common.actions.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </GenericPage>
    );
  }

  // ─── FORM VIEW ──────────────────────────────────────────────────────────────

  const isEditing = !!(formData as any).GRP_IDF || !!formData.id;
  const formPayload = groupToDto(formData);
  const selectedCourse = courses.find(c => c.id === formData.course_id);

  return (
    <GenericPage
      title={
        isEditing
          ? t('academy.groups.edit_title')
          : t('academy.groups.create_title')
      }
      icon={<KanbanIcon />}
      footerEnabled={true}
      footerInfo={{
        lastUpdate: formData.last_updt || '-',
        userLib: formData.user_lib || '-',
      }}
      additionalActions={
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={backToList}>
          {t('academy.groups.back_to_list')}
        </Button>
      }
      crudConfig={{
        entityKey: ENTITY_KEY,
        actionSuffix: ACTION_SUFFIX,
        primaryKey: PRIMARY_KEY,
      }}
      data={formPayload}
      onDataChange={() => handleDataChange()}

      onValidate={handleValidate}
      onClear={handleClear}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto', pb: 4 }}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {t('academy.groups.section_info')}
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                label={t('academy.groups.name_label')}
                size="small"
                fullWidth
                required
                sx={fsx}
                value={formData.name ?? ''}
                onChange={e => setField('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label={t('academy.groups.course_template')}
                size="small"
                fullWidth
                required
                sx={fsx}
                value={formData.course_id ?? ''}
                onChange={e => setField('course_id', Number(e.target.value))}
              >
                {courses.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.title} ({currencySymbol}{c.price} - {c.total_hours}h)
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {selectedCourse && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block">
                    {t('academy.courses.course_preview')}:
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold">{selectedCourse.title}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip label={`${currencySymbol}${selectedCourse.price}`} color="success" size="small" />
                    <Chip label={`${selectedCourse.total_hours}h`} color="info" size="small" />
                  </Box>
                  {selectedCourse.description && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {selectedCourse.description}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                select
                label={t('academy.groups.primary_teacher')}
                size="small"
                fullWidth
                required
                sx={fsx}
                value={formData.teacher_id ?? ''}
                onChange={e => setField('teacher_id', Number(e.target.value))}
              >
                {teachers.map(te => (
                  <MenuItem key={te.id} value={te.id}>{te.full_name} ({te.specialization})</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.groups.max_capacity')}
                type="number"
                size="small"
                fullWidth
                required
                sx={fsx}
                value={formData.max_capacity ?? 20}
                onChange={e => setField('max_capacity', Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t('academy.groups.table.status')}
                size="small"
                fullWidth
                sx={fsx}
                value={formData.status ?? 'Upcoming'}
                onChange={e => setField('status', e.target.value as GroupStatus)}
              >
                <MenuItem value="Upcoming">{t('academy.groups.status.upcoming')}</MenuItem>
                <MenuItem value="Ongoing">{t('academy.groups.status.ongoing')}</MenuItem>
                <MenuItem value="Completed">{t('academy.groups.status.completed')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.groups.start_date')}
                type="date"
                size="small"
                fullWidth
                sx={fsx}
                value={formData.start_date ?? ''}
                onChange={e => setField('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.groups.expected_end_date')}
                type="date"
                size="small"
                fullWidth
                sx={fsx}
                value={formData.expected_end_date ?? ''}
                onChange={e => setField('expected_end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Paper>

        {(formData.dos_code || formData.dos_lib) && (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('academy.groups.section_dossier')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.groups.form.dossier_code')}
                  size="small"
                  fullWidth
                  disabled
                  sx={fsx}
                  value={formData.dos_code ?? ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.groups.form.dossier_lib')}
                  size="small"
                  fullWidth
                  disabled
                  sx={fsx}
                  value={formData.dos_lib ?? ''}
                />
              </Grid>
            </Grid>
          </Paper>
        )}
      </Box>
    </GenericPage>
  );
};

export default GroupsPage;
